import { TicketItemGroupType } from "@shared/modules/basket/types/common/ticket-item-group/ticket-item-group-type";
import { enums } from "@shared/constants";
import { GtagItemGroupType } from "@shared/lib/analytics/types/analytics-types";
import gtag from "@shared/lib/analytics/gtag";
import { IBasket } from "@shared/modules/basket/interfaces/IBasket";
import { TicketBasket } from "@shared/modules/basket/types";

// The formatters below will massage item_groups to be compatible with external analytics services
// like Google Tag Manager and Facebook Pixel. We need different formatting for our internal analytics

/**
 * Item types that should be excluded from external analytics tracking.
 * These are typically ancillary fees/charges (protection) that shouldn't
 * be tracked as products in analytics platforms like Google Analytics and Facebook Pixel.
 */
const ANALYTICS_EXCLUDED_TYPES: string[] = [
  enums.ITEM_TYPES_MAP.ITEM_TYPE_PROTECTION.id,
];

/**
 * Subtract hidden amounts (protection charges) from the total price for analytics tracking.
 * Similar to subtractHiddenAmounts in price-utils.ts, this ensures analytics
 * revenue metrics exclude ancillary protection charges.
 */
const subtractHiddenAmounts = (
  basket: TicketBasket | IBasket,
  totalPrice: string | number | undefined
): string => {
  const total = parseFloat(String(totalPrice || "0"));
  let protectionFees: string | undefined;
  if ("protection_fees" in basket) {
    protectionFees = basket.protection_fees;
  } else if ("protectionFees" in basket) {
    protectionFees = basket.protectionFees;
  }
  const protection = parseFloat(String(protectionFees || "0"));
  return (total - protection).toFixed(2);
};

export const externalFormatter = {
  formatItemGroup: (group: TicketItemGroupType): GtagItemGroupType => {
    const getTrackingParams = () => {
      switch (group.item_type) {
        case enums.ITEM_TYPES_MAP.ITEM_TYPE_PRODUCT.id:
          return [
            (group.product_attribute || group.id) ?? -1,
            group.item_name ?? "",
            group.item_variant ?? "",
            group.item_type,
          ] as const;
        case enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id:
          return [
            (group.membership_level || group.id) ?? -1,
            group.item_name ?? "",
            group.item_variant ?? "",
            group.item_type,
          ] as const;
        case enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id:
          return [
            (group.type || group.id) ?? -1,
            group.item_name ?? "",
            group.item_variant ?? "",
            group.item_type,
          ] as const;
        case enums.ITEM_TYPES_MAP.ITEM_TYPE_DONATION.id:
          return [
            group.charity_id ?? -1,
            group.description ?? "",
            group.charity_id ?? "",
            group.item_type,
          ] as const;
        default:
          throw new Error(`not handling item group type ${group.item_type}`);
      }
    };

    const formattedItem = gtag.formatTrackingData(
      ...(getTrackingParams() as Parameters<typeof gtag.formatTrackingData>)
    );
    return {
      ...group,
      ...formattedItem,
    };
  },

  ibasketToGtagBasket: (basket: IBasket) => {
    return {
      ...basket,
      total_price: subtractHiddenAmounts(basket, basket.totalPrice),
      user_id: basket.userId,
      item_groups: basket.itemGroups
        .filter(
          (group) =>
            group.item_type &&
            !ANALYTICS_EXCLUDED_TYPES.includes(group.item_type)
        )
        .map(externalFormatter.formatItemGroup),
    };
  },

  ticketBasketToGtagBasket: (basket: TicketBasket) => {
    return {
      ...basket,
      total_price: subtractHiddenAmounts(basket, basket.total_price),
      item_groups: basket.item_groups
        ?.filter(
          (group) =>
            group.item_type &&
            !ANALYTICS_EXCLUDED_TYPES.includes(group.item_type)
        )
        .map(externalFormatter.formatItemGroup),
    };
  },
};
